import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User } from '@/types';
import { login as apiLogin, setToken, removeToken, getToken, isTokenExpired, getTokenRemainingMs, redirectToLogin } from '@/services/api';
import { toast } from 'sonner';
import { zugangVergessen } from '@/hooks/useZugang';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'shk_app_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const performLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    removeToken();
    // Rechte-Cache leeren, sonst erbt der naechste Nutzer am selben Geraet die
    // Rechte des vorigen, bis der Browser neu geladen wird.
    zugangVergessen();
    // Navigate to login if not already there — merkt sich die aktuelle Seite (?redirect=)
    if (window.location.pathname !== '/login') {
      redirectToLogin();
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem(STORAGE_KEY);
    const token = getToken();
    
    if (storedUser && token) {
      // Check if already expired on load
      if (isTokenExpired(token)) {
        performLogout();
      } else {
        try {
          const parsed = JSON.parse(storedUser);
          setUser(parsed);
        } catch {
          performLogout();
        }
      }
    } else if (!token) {
      localStorage.removeItem(STORAGE_KEY);
    }
    setIsLoading(false);
  }, [performLogout]);

  // Periodic token expiration check (every 30 seconds)
  // Seit 09.06.: Vorwarnung 10 Min vor Ablauf statt nur hartem Rauswurf —
  // gibt dem User Zeit, offene Eingaben zu speichern. Redirect merkt sich die Seite.
  const expiryWarnedRef = useRef(false);
  useEffect(() => {
    const interval = setInterval(() => {
      const token = getToken();
      if (!token) return;
      const remaining = getTokenRemainingMs(token);
      if (remaining <= 0) {
        console.log('🔒 Token expired, auto-logging out');
        performLogout();
        return;
      }
      if (remaining < 10 * 60_000 && !expiryWarnedRef.current) {
        expiryWarnedRef.current = true;
        toast.warning(
          'Deine Anmeldung läuft in wenigen Minuten ab. Bitte offene Eingaben abschließen — danach wirst du zur Anmeldung geleitet und kommst direkt hierher zurück.',
          { duration: 15_000 },
        );
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [performLogout]);

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    
    try {
      const response = await apiLogin(username, password);
      
      if (response.success && response.token && response.user) {
        const u = response.user;
        // Login liefert vorname/nachname, aber kein "name" — hier zusammensetzen,
        // damit AppHeader/Home den Namen anzeigen (vorher überall leer).
        const displayName =
          u.name || [u.vorname, u.nachname].filter(Boolean).join(' ').trim() || u.email || '';
        const authenticatedUser: User = {
          ...u,
          name: displayName,
          token: response.token,
        };
        
        setUser(authenticatedUser);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(authenticatedUser));
        setToken(response.token);
        setIsLoading(false);
        return { success: true };
      }
      
      setIsLoading(false);
      return { success: false, error: response.error || 'Ungültiger Benutzername oder Passwort' };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: 'Verbindungsfehler. Bitte versuchen Sie es erneut.' };
    }
  };

  const logout = performLogout;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Avoid hard-crashing the whole app if a component renders outside the provider.
    // This should not happen in normal operation, but it can during HMR/edge cases.
    if (import.meta.env.DEV) {
      console.warn("useAuth was called outside AuthProvider");
    }

    return {
      user: null,
      isLoading: false,
      isAuthenticated: false,
      login: async () => ({
        success: false,
        error: "Auth nicht initialisiert. Bitte Seite neu laden.",
      }),
      logout: () => {},
    };
  }
  return context;
}
